// A Saju book page's text boxes (canvas_scenes.py `boxes`) as the narrative components that draw them (specs/011 §12):
// the boxes holding one scope's manuscript — a 대운, a year, the product's own text — make one component, and the boxes
// holding only what the chart computes make the page's `book` component. A box of frame words, catalogue lines and layout
// numbers alone stays the page's own text. The wealth and personality books' readings of the sample's chart, kept in the
// frame copy (PRODUCT_READING), are the product's manuscript on its pages; a part title borrowing one as a heading keeps it.
import { asPrinted } from './print-wrap.mjs';
import { NARRATIVE_COMPONENT, PRODUCT_READING, narrativeSnapshot } from './component-saju-narrative.mjs';
import { factsHash, scopeFacts } from './template-saju-facts.mjs';

const MANUSCRIPT = /^(?:series\/(daeun|seun)\/(\d+)\/|doc\/|frame\/native-(?:wealth|personality)\.)/;
const failure = message => new Error(`책의 글상자를 원고 컴포넌트로 옮기지 못했습니다: ${message}`);

/** The scope a manuscript address was written for: series/daeun/6/story → 6대운, series/seun/2026/… → 2026년, doc/… and a product reading → the product. */
export function manuscriptScope(address, product) {
  const match = MANUSCRIPT.exec(address);
  if (!match) throw failure(`원고 주소가 아닙니다 (${address})`);
  if (!match[1]) return { series: 'product', product };
  return match[1] === 'daeun' ? { series: 'daeun', period: Number(match[2]) } : { series: 'seun', year: Number(match[2]) };
}
export const scopeKey = scope => scope.series === 'daeun' ? `daeun:${scope.period}` : scope.series === 'seun' ? `seun:${scope.year}` : `${scope.series}:${scope.product}`;

// A reading kept in the frame copy, on the product's own page: its manuscript slot ({ text: address, literal }).
const asManuscript = (box, segment) => segment.address && PRODUCT_READING.test(segment.address) && !box.id.startsWith('layout:part-title/')
  ? { text: segment.address, literal: segment.literal } : segment;

/**
 * The narrative components of one exported page: [{ scope, boxes, slots, figures: Map, lineIds }]. `boxes` keep their
 * type and their segments, a manuscript segment naming its slot ({ text: address }) and a figure its key and what the book
 * printed ({ figure, literal }); `slots` are the manuscripts' printed words; `lineIds` the scene's line elements the
 * components draw instead.
 */
export function pageNarratives(page, product) {
  const groups = new Map();
  for (const exported of page.boxes ?? []) {
    if (exported.computed) throw failure(`${page.number}쪽 ${exported.id} 에 설명되지 않은 계산값이 있습니다 (${exported.residual})`);
    if (!Array.isArray(exported.segments)) throw failure(`${page.number}쪽 ${exported.id} 에 조각이 없습니다`);
    const box = { ...exported, segments: exported.segments.map(segment => asManuscript(exported, segment)) };
    if (box.segments.map(segment => segment.literal).join('') !== box.text) throw failure(`${page.number}쪽 ${box.id} 의 조각이 글과 맞지 않습니다`);
    const manuscripts = [...new Set(box.segments.filter(segment => segment.text).map(segment => scopeKey(manuscriptScope(segment.text, product))))];
    const figures = box.segments.some(segment => segment.figure);
    if (manuscripts.length > 1) throw failure(`${page.number}쪽 ${box.id} 가 여러 범위의 원고를 담습니다 (${manuscripts.join(', ')})`);
    if (!manuscripts.length && !figures) continue;
    const scope = manuscripts.length ? manuscriptScope(box.segments.find(segment => segment.text).text, product) : { series: 'book', product };
    const key = scopeKey(scope);
    if (!groups.has(key)) groups.set(key, { scope, boxes: [], slots: {}, figures: new Map(), lineIds: [] });
    const group = groups.get(key);
    for (const segment of box.segments) {
      if (segment.text) {
        if (Object.hasOwn(group.slots, segment.text) && group.slots[segment.text] !== segment.literal) throw failure(`${page.number}쪽의 ${segment.text} 가 두 곳에서 다르게 찍혔습니다`);
        group.slots[segment.text] = segment.literal;
      } else if (segment.figure) {
        if (group.figures.has(segment.figure) && group.figures.get(segment.figure) !== segment.literal) throw failure(`${page.number}쪽의 ${segment.figure} 가 두 곳에서 다르게 찍혔습니다`);
        group.figures.set(segment.figure, segment.literal);
      }
    }
    group.boxes.push({ id: box.id, x: box.x, y: box.y, width: box.width, size: box.size, font: box.font, color: box.color,
      height: box.height, lines: box.lines, center: box.center, leading: box.leading,
      segments: box.segments.map(segment => segment.text ? { text: segment.text } : segment.figure ? { figure: segment.figure, literal: segment.literal } : { ...segment }) });
    group.lineIds.push(...box.elements);
  }
  return [...groups.values()];
}

/**
 * Checks a page's printed figures against the figures module's for the same person: the book and the Studio make the
 * same text, or the import stops rather than draw another text than the book printed.
 */
export function checkPrintedFigures(narrative, figures, where) {
  for (const [key, printed] of narrative.figures) {
    if (!Object.hasOwn(figures, key)) throw failure(`${where}: 공통 모듈이 ${key} 를 만들지 못합니다`);
    if (asPrinted(figures[key]) !== printed) throw failure(`${where}: ${key} 가 책과 다릅니다 (책 「${printed}」, 모듈 「${asPrinted(figures[key])}」)`);
  }
}

/** The sample person's name and data id as the book printed them (its page label and colophon figures), when it did. */
export function printedPerson(pages) {
  const person = {};
  for (const page of pages) for (const box of page.boxes ?? []) for (const segment of box.segments ?? []) {
    if (segment.figure === 'person/name') person.name = segment.literal;
    if (segment.figure === 'book/data-id') person.dataId = segment.literal;
  }
  return person;
}

const DRAWN = ['text', 'x', 'y', 'width', 'height', 'fontSize', 'fontFamily', 'fontWeight', 'color'];
/** A component draws exactly the lines it replaces — text, place, size, face and colour — or the import stops. */
export function checkDrawnLines(drawn, printed, where) {
  if (drawn.length !== printed.length) throw failure(`${where}: 컴포넌트가 ${drawn.length}줄을 그리는데 책은 ${printed.length}줄입니다`);
  drawn.forEach((line, index) => {
    const field = DRAWN.find(key => line[key] !== printed[index][key]);
    if (field) throw failure(`${where}: ${index + 1}번째 줄의 ${field} 가 책과 다릅니다 (책 ${JSON.stringify(printed[index][field])}, 컴포넌트 ${JSON.stringify(line[field])})`);
  });
}

/**
 * Whether a page's own words — not a manuscript, a figure or a layout number — print the year the book reads as now (the
 * 대운 timeline's "현재 · 2026년" plaque): printed for another year, the page is the sample's (book-text.mjs).
 */
export function pageYearBound(page, year) {
  return (page.boxes ?? []).some(box => (box.segments ?? []).some(segment => !segment.text && !segment.figure && !segment.layout
    && typeof segment.literal === 'string' && segment.literal.includes(String(year))));
}

/**
 * A page's narrative components for the book's sample person: [{ props, snapshot, drawn, lineIds }], each checked to draw
 * exactly the lines it replaces. `context` is the import's: { product, subject, book, figures (the book's bookFigures),
 * person (printedPerson), frame (the frame words the book was built with that differ from the Studio's, among those the
 * figures fill: { key: text }), hashes (a Map the pages share: a scope's facts are hashed once) }.
 */
export async function pageNarrativeComponents(page, { product, subject, book, figures, person, frame = {}, hashes }) {
  const byId = new Map(page.scene.elements.map(element => [element.id, element]));
  const components = [];
  for (const narrative of pageNarratives(page, product)) {
    const where = `${page.number}쪽`, key = scopeKey(narrative.scope);
    checkPrintedFigures(narrative, figures, where);
    if (narrative.scope.series !== 'book' && !hashes.has(key)) hashes.set(key, await factsHash(scopeFacts(book, narrative.scope)));
    const usesPerson = [...narrative.figures.keys()].some(figure => figure.startsWith('person/') || figure === 'book/data-id');
    const props = NARRATIVE_COMPONENT.validateProps({ ...subject, scope: narrative.scope, boxes: narrative.boxes,
      text: { slots: narrative.slots, factsHash: narrative.scope.series === 'book' ? null : hashes.get(key), review: { state: 'sample' } },
      ...(usesPerson ? { person } : {}), ...(narrative.figures.size && Object.keys(frame).length ? { frame } : {}) });
    const snapshot = await narrativeSnapshot(props, book, figures);
    NARRATIVE_COMPONENT.validateSnapshot(snapshot);
    const drawn = NARRATIVE_COMPONENT.render(props, snapshot);
    checkDrawnLines(drawn.elements, narrative.lineIds.map(id => byId.get(id)), where);
    components.push({ props, snapshot, drawn, lineIds: narrative.lineIds });
  }
  return components;
}
